import React, { useState, useEffect } from 'react';
import { contactData } from '../../data/mock';
import { MessageCircle, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    tripType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Listen for service selection from Services component
  useEffect(() => {
    const handleServiceSelected = (event) => {
      const { tripType } = event.detail;
      if (tripType) {
        setFormData((prev) => ({ ...prev, tripType }));
      }
    };

    window.addEventListener('serviceSelected', handleServiceSelected);
    return () => {
      window.removeEventListener('serviceSelected', handleServiceSelected);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        trip_type: formData.tripType,
        message: formData.message,
      });
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', tripType: '', message: '' });
    } catch (error) {
      console.error('Failed to submit contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-[#0a1628] py-16 sm:py-24 lg:py-32">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 lg:gap-24">
          {/* Left Side - Info */}
          <div className="text-center lg:text-left">
            <p
              className="text-[#c9a96e] text-[10px] sm:text-[11px] tracking-[0.3em] uppercase mb-3 sm:mb-4"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Get in Touch
            </p>
            <h2
              className="text-white text-2xl sm:text-3xl md:text-4xl font-light mb-4 sm:mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {contactData.headline}
            </h2>
            <p
              className="text-white/70 text-base sm:text-lg mb-3 sm:mb-4 font-light"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {contactData.subheadline}
            </p>
            <p
              className="text-white/40 text-sm sm:text-base mb-8 sm:mb-12 font-light"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {contactData.description}
            </p>

            {/* Contact Methods */}
            <div className="flex flex-col sm:flex-row lg:flex-col gap-4 sm:gap-8 lg:gap-6 justify-center lg:justify-start">
              {/* WhatsApp */}
              <a
                href={`https://wa.me/${contactData.whatsapp.replace(/\s/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 sm:gap-4 group justify-center lg:justify-start"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 border border-[#c9a96e]/40 flex items-center justify-center transition-all duration-300 group-hover:border-[#c9a96e] group-hover:bg-[#c9a96e]/10">
                  <MessageCircle size={16} className="text-[#c9a96e] sm:w-[18px] sm:h-[18px]" />
                </div>
                <div className="text-left">
                  <p
                    className="text-[9px] sm:text-[10px] text-[#c9a96e] mb-0.5 sm:mb-1 uppercase tracking-wider"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    WhatsApp
                  </p>
                  <p
                    className="text-white text-xs sm:text-sm group-hover:text-[#c9a96e] transition-colors duration-300"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {contactData.whatsapp}
                  </p>
                </div>
              </a>

              {/* Email */}
              <a
                href={`mailto:${contactData.email}`}
                className="flex items-center gap-3 sm:gap-4 group justify-center lg:justify-start"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 border border-[#c9a96e]/40 flex items-center justify-center transition-all duration-300 group-hover:border-[#c9a96e] group-hover:bg-[#c9a96e]/10">
                  <Mail size={16} className="text-[#c9a96e] sm:w-[18px] sm:h-[18px]" />
                </div>
                <div className="text-left">
                  <p
                    className="text-[9px] sm:text-[10px] text-[#c9a96e] mb-0.5 sm:mb-1 uppercase tracking-wider"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Email
                  </p>
                  <p
                    className="text-white text-xs sm:text-sm group-hover:text-[#c9a96e] transition-colors duration-300 break-all"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {contactData.email}
                  </p>
                </div>
              </a>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-white/5 backdrop-blur-sm p-5 sm:p-8 lg:p-10 border border-white/10">
            <h3
              className="text-white text-lg sm:text-xl font-light mb-6 sm:mb-8"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Send a Message
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {/* Name */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-[9px] sm:text-[10px] text-white/40 mb-1.5 sm:mb-2 uppercase tracking-wider"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/20 px-3 sm:px-4 py-2.5 sm:py-3 text-white text-sm focus:outline-none focus:border-[#c9a96e] transition-colors duration-300 placeholder-white/30"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                />
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-[9px] sm:text-[10px] text-white/40 mb-1.5 sm:mb-2 uppercase tracking-wider"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/20 px-3 sm:px-4 py-2.5 sm:py-3 text-white text-sm focus:outline-none focus:border-[#c9a96e] transition-colors duration-300 placeholder-white/30"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                />
              </div>

              {/* Trip Type */}
              <div>
                <label
                  htmlFor="tripType"
                  className="block text-[9px] sm:text-[10px] text-white/40 mb-1.5 sm:mb-2 uppercase tracking-wider"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Trip Type
                </label>
                <select
                  id="tripType"
                  name="tripType"
                  value={formData.tripType}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/5 border border-white/20 px-3 sm:px-4 py-2.5 sm:py-3 text-white text-sm focus:outline-none focus:border-[#c9a96e] transition-colors duration-300 appearance-none cursor-pointer"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  <option value="" className="bg-[#0a1628]">Select an option</option>
                  <option value="flights" className="bg-[#0a1628]">Flights & Airline Tickets</option>
                  <option value="cruises" className="bg-[#0a1628]">Cruise Packages</option>
                  <option value="hotels" className="bg-[#0a1628]">Hotels & Resorts</option>
                  <option value="itinerary" className="bg-[#0a1628]">Custom Itinerary</option>
                  <option value="other" className="bg-[#0a1628]">Other</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label
                  htmlFor="message"
                  className="block text-[9px] sm:text-[10px] text-white/40 mb-1.5 sm:mb-2 uppercase tracking-wider"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Your Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={3}
                  className="w-full bg-white/5 border border-white/20 px-3 sm:px-4 py-2.5 sm:py-3 text-white text-sm focus:outline-none focus:border-[#c9a96e] transition-colors duration-300 resize-none placeholder-white/30"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  placeholder="Tell us about your travel plans..."
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 text-[11px] sm:text-[12px] font-light tracking-[0.15em] uppercase bg-[#c9a96e] text-[#0a1628] hover:bg-[#d4b87a] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    Send Message
                    <Send size={12} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
